import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  WHATSAPP_NUM,
  COURSE_PRICE_NUM,
  SUMMER_COUPON_CODE,
  SUMMER_COUPON_DISCOUNT,
} from '../constants';

const API_KEY = process.env.REACT_APP_API_KEY || '';
const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || '';

const CLASSES = [
  '5th-9th',
  '10th',
  'Intermediate (11th)',
  'Intermediate (12th)',
  'Diploma',
  'Degree - 1st Year',
  'Degree - 2nd Year',
  'Degree - 3rd Year',
  'Other',
];

const COURSE_OPTIONS = [
  {
    id: 'python',
    icon: <img src="/python.png" alt="Python" style={{ height: 28 }} />,
    title: 'Python + AI Course',
    subtitle: '6–8 Weeks · Beginner · Online Live',
    price: COURSE_PRICE_NUM,
  },
  {
    id: 'webdev',
    icon: '🌐',
    title: 'Web Development Course',
    subtitle: '6–8 Weeks · Beginner · Online Live',
    price: COURSE_PRICE_NUM,
  },
  {
    id: 'msoffice',
    icon: '💼',
    title: 'MS Office & AI Tools',
    subtitle: '6–8 Weeks · Beginner · Online Live',
    price: COURSE_PRICE_NUM,
  },
];

const TIMINGS = [
  'Morning (9AM - 12PM)',
  'Afternoon (1PM - 4PM)',
  'Evening (5PM - 8PM)',
];

// ── BatchCalendar ─────────────────────────────────────────────────────────────
const DAYS_HEADER = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const ALLOWED_DOW = new Set([1, 3, 5]); // Mon, Wed, Fri

function BatchCalendar({ selected, onChange }) {
  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const [viewYear, setViewYear]   = useState(today.getFullYear());
  const [viewMonth, setViewMonth] = useState(today.getMonth()); // 0-indexed

  // Clamp navigation: only current month and next month
  const minMonth = today.getMonth();
  const minYear  = today.getFullYear();
  const maxMonth = minMonth + 1 > 11 ? 0       : minMonth + 1;
  const maxYear  = minMonth + 1 > 11 ? minYear + 1 : minYear;

  function prevMonth() {
    if (viewYear === minYear && viewMonth === minMonth) return;
    if (viewMonth === 0) { setViewMonth(11); setViewYear(y => y - 1); }
    else setViewMonth(m => m - 1);
  }

  function nextMonth() {
    if (viewYear === maxYear && viewMonth === maxMonth) return;
    if (viewMonth === 11) { setViewMonth(0); setViewYear(y => y + 1); }
    else setViewMonth(m => m + 1);
  }

  const canGoPrev = !(viewYear === minYear && viewMonth === minMonth);
  const canGoNext = !(viewYear === maxYear && viewMonth === maxMonth);

  // Build grid: blank cells before 1st + days of month
  const firstDOW = new Date(viewYear, viewMonth, 1).getDay();
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
  const cells = [
    ...Array(firstDOW).fill(null),
    ...Array.from({ length: daysInMonth }, (_, i) => i + 1),
  ];

  const monthLabel = new Date(viewYear, viewMonth).toLocaleString('en-IN', {
    month: 'long',
    year: 'numeric',
  });

  function handleDay(day) {
    const d = new Date(viewYear, viewMonth, day);
    d.setHours(0, 0, 0, 0);
    if (d < today) return;
    if (!ALLOWED_DOW.has(d.getDay())) return;
    // Format: "12 May 2026"
    const label = d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
    onChange(label === selected ? '' : label);
  }

  return (
    <div
      style={{
        border: '1.5px solid #d8dde6',
        borderRadius: 14,
        overflow: 'hidden',
        background: '#fff',
        userSelect: 'none',
      }}
    >
      {/* Month nav */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: 'var(--navy)',
          padding: '10px 16px',
          color: '#fff',
        }}
      >
        <button
          type="button"
          onClick={prevMonth}
          disabled={!canGoPrev}
          style={{
            background: 'none',
            border: 'none',
            color: canGoPrev ? '#fff' : 'rgba(255,255,255,0.25)',
            fontSize: 18,
            cursor: canGoPrev ? 'pointer' : 'not-allowed',
            padding: '0 6px',
            lineHeight: 1,
          }}
        >
          ‹
        </button>
        <span style={{ fontWeight: 800, fontSize: 15 }}>{monthLabel}</span>
        <button
          type="button"
          onClick={nextMonth}
          disabled={!canGoNext}
          style={{
            background: 'none',
            border: 'none',
            color: canGoNext ? '#fff' : 'rgba(255,255,255,0.25)',
            fontSize: 18,
            cursor: canGoNext ? 'pointer' : 'not-allowed',
            padding: '0 6px',
            lineHeight: 1,
          }}
        >
          ›
        </button>
      </div>

      {/* Day-of-week headers */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, 1fr)',
          background: 'rgba(27,58,107,0.06)',
          borderBottom: '1px solid #e8edf4',
        }}
      >
        {DAYS_HEADER.map((d) => (
          <div
            key={d}
            style={{
              textAlign: 'center',
              padding: '7px 0',
              fontSize: 11,
              fontWeight: 800,
              letterSpacing: '0.04em',
              color: ['Mon', 'Wed', 'Fri'].includes(d) ? 'var(--navy)' : '#bbb',
              textTransform: 'uppercase',
            }}
          >
            {d}
          </div>
        ))}
      </div>

      {/* Day cells */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, 1fr)',
          padding: '8px 6px 10px',
          gap: 3,
        }}
      >
        {cells.map((day, idx) => {
          if (!day) return <div key={`blank-${idx}`} />;

          const d = new Date(viewYear, viewMonth, day);
          d.setHours(0, 0, 0, 0);
          const dow        = d.getDay();
          const isAllowed  = ALLOWED_DOW.has(dow);
          const isPast     = d < today;
          const dateLabel  = d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
          const isSelected = dateLabel === selected;
          const isToday    = d.getTime() === today.getTime();
          const isDisabled = isPast || !isAllowed;

          return (
            <button
              key={day}
              type="button"
              onClick={() => handleDay(day)}
              disabled={isDisabled}
              style={{
                width: '100%',
                aspectRatio: '1',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: 8,
                border: isSelected
                  ? '2px solid var(--navy)'
                  : isToday && isAllowed
                  ? '1.5px solid rgba(27,58,107,0.35)'
                  : '2px solid transparent',
                background: isSelected
                  ? 'var(--navy)'
                  : isAllowed && !isPast
                  ? 'rgba(240,180,41,0.10)'
                  : 'transparent',
                color: isSelected
                  ? '#fff'
                  : isDisabled
                  ? '#ccc'
                  : isAllowed
                  ? 'var(--navy)'
                  : '#999',
                fontSize: 13,
                fontWeight: isAllowed && !isDisabled ? 800 : 400,
                cursor: isDisabled ? 'not-allowed' : 'pointer',
                transition: 'all 0.12s ease',
              }}
            >
              {day}
            </button>
          );
        })}
      </div>

      {/* Legend */}
      <div
        style={{
          display: 'flex',
          gap: 16,
          padding: '8px 14px 12px',
          fontSize: 12,
          color: 'var(--gray)',
          borderTop: '1px solid #f0f0f0',
          flexWrap: 'wrap',
        }}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <span
            style={{
              width: 12,
              height: 12,
              borderRadius: 3,
              background: 'rgba(240,180,41,0.18)',
              border: '1.5px solid rgba(240,180,41,0.5)',
              display: 'inline-block',
            }}
          />
          Available (Mon / Wed / Fri)
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <span
            style={{
              width: 12,
              height: 12,
              borderRadius: 3,
              background: 'var(--navy)',
              display: 'inline-block',
            }}
          />
          Selected
        </span>
      </div>
    </div>
  );
}

// ── helpers ─────────────────────────────────────────────────────────────────
function fmt(num) {
  return '₹' + num.toLocaleString('en-IN');
}

function Field({ label, error, children }) {
  return (
    <div className="form-group">
      <label className="form-label">{label}</label>
      {children}
      {error && <div className="form-error">Error: {error}</div>}
    </div>
  );
}

function useRecaptcha() {
  const loaded = useRef(false);

  useEffect(() => {
    if (!RECAPTCHA_SITE_KEY || loaded.current) return;
    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`;
    script.async = true;
    document.head.appendChild(script);
    loaded.current = true;
  }, []);

  const getToken = (action) =>
    new Promise((resolve, reject) => {
      if (!RECAPTCHA_SITE_KEY) return resolve(null);
      if (!window.grecaptcha) return reject(new Error('reCAPTCHA not loaded'));
      window.grecaptcha.ready(() => {
        window.grecaptcha.execute(RECAPTCHA_SITE_KEY, { action }).then(resolve).catch(reject);
      });
    });

  return { getToken };
}

// ── component ────────────────────────────────────────────────────────────────
export default function RegistrationPage() {
  const [form, setForm] = useState({
    name: '',
    parent: '',
    phone: '',
    email: '',
    cls: '',
    laptop: '',
    courses: [],   // array of course ids
    batchDate: '',
    timing: '',
    note: '',
  });

  const [couponInput, setCouponInput]   = useState('');
  const [couponApplied, setCouponApplied] = useState(false);
  const [couponError, setCouponError]   = useState('');

  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState('idle'); // idle | sending | success | error
  const { getToken } = useRecaptcha();

  // ── course toggle ──────────────────────────────────────────────
  function toggleCourse(id) {
    setForm((f) => ({
      ...f,
      courses: f.courses.includes(id)
        ? f.courses.filter((c) => c !== id)
        : [...f.courses, id],
    }));
    setErrors((e) => ({ ...e, courses: undefined }));
  }

  // ── pricing ────────────────────────────────────────────────────
  const originalTotal = useMemo(
    () => form.courses.length * COURSE_PRICE_NUM,
    [form.courses]
  );

  const discountAmt = useMemo(
    () => (couponApplied ? Math.round(originalTotal * SUMMER_COUPON_DISCOUNT) : 0),
    [couponApplied, originalTotal]
  );

  const finalTotal = originalTotal - discountAmt;

  const selectedCourseNames = useMemo(
    () => form.courses.map((id) => COURSE_OPTIONS.find((c) => c.id === id)?.title).join(', '),
    [form.courses]
  );

  // ── coupon ─────────────────────────────────────────────────────
  function applyCoupon() {
    if (couponInput.trim().toUpperCase() === SUMMER_COUPON_CODE) {
      setCouponApplied(true);
      setCouponError('');
    } else {
      setCouponApplied(false);
      setCouponError('Invalid coupon code. Try ' + SUMMER_COUPON_CODE);
    }
  }

  function removeCoupon() {
    setCouponApplied(false);
    setCouponInput('');
    setCouponError('');
  }

  // ── form helpers ───────────────────────────────────────────────
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  function validate() {
    const e = {};
    if (!form.name.trim())   e.name   = 'Student name is required';
    if (!form.parent.trim()) e.parent = 'Parent / guardian name is required';
    if (!form.phone.trim() || form.phone.replace(/\D/g, '').length < 10) {
      e.phone = 'Enter a valid 10-digit phone number';
    }
    if (!form.email.trim() || !form.email.includes('@')) e.email = 'Valid email address required';
    if (!form.cls)    e.cls    = 'Please select current class';
    if (!form.laptop) e.laptop = 'Please select an option';
    if (form.courses.length === 0) e.courses = 'Please select at least one course';
    if (!form.batchDate) e.batchDate = 'Please select a batch start date';
    return e;
  }


  async function handleSubmit() {
    const e = validate();
    if (Object.keys(e).length > 0) {
      setErrors(e);
      return;
    }
    setStatus('sending');
    setErrors({});

    try {
      const recaptchaToken = await getToken('course_registration');
      const sharedParams = {
        registration_type: 'course_registration',
        student_name:  form.name,
        parent_name:   form.parent,
        phone:         form.phone,
        email:         form.email,
        cls:           form.cls,
        laptop:        form.laptop,
        course:        selectedCourseNames,
        batch_date:    form.batchDate,
        demo_date:     form.batchDate,
        timing:        form.timing || 'Flexible',
        fee_display:   form.courses.length > 0 ? fmt(finalTotal) : 'Not selected',
        coupon_used:   couponApplied ? SUMMER_COUPON_CODE : 'None',
        payment_note:  'Payment can be made in first 3 classes (first class includes demo).',
        note:          form.note || 'None',
        submitted_at:  new Date().toLocaleString('en-IN'),
        recaptchaToken,
      };

      const res = await fetch(
        'https://us-central1-remote-camera-control.cloudfunctions.net/sendCourseRegistrationEmail',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-api-key': API_KEY,
          },
          body: JSON.stringify(sharedParams),
        }
      );

      if (!res.ok) throw new Error('Failed');
      setStatus('success');
    } catch (err) {
      console.error(err);
      setStatus('error');
    }
  }

  // ── success screen ─────────────────────────────────────────────
  if (status === 'success') {
    const waMsg =
      `Hi! I just completed course registration at VRK Tech Academy.\n` +
      `Name: ${form.name}\nPhone: ${form.phone}\nClass: ${form.cls}\n` +
      `Course(s): ${selectedCourseNames}\nBatch Date: ${form.batchDate}\n` +
      `Fee: ${fmt(finalTotal)}${couponApplied ? ` (coupon ${SUMMER_COUPON_CODE} applied)` : ''}\n` +
      `Payment plan: first 3 classes`;

    return (
      <div
        style={{
          paddingTop: 64,
          minHeight: '80vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'var(--cream)',
        }}
      >
        <div style={{ textAlign: 'center', padding: '40px 24px', maxWidth: 620 }}>
          <h2 style={{ color: 'var(--navy)', fontSize: 30, fontWeight: 900, marginBottom: 12 }}>
            Registration Successful
          </h2>
          <p style={{ color: 'var(--gray)', fontSize: 16, lineHeight: 1.75, marginBottom: 18 }}>
            Thank you, <strong>{form.name}</strong>. Your seat request is saved for
            <strong> {form.batchDate}</strong>.
          </p>
          <p style={{ color: 'var(--gray)', fontSize: 15, lineHeight: 1.7, marginBottom: 30 }}>
            Fee for selected course(s): <strong>{fmt(finalTotal)}</strong>
            {couponApplied && (
              <span style={{ color: '#1a5a2e', fontWeight: 700 }}>
                {' '}(50% summer discount applied 🎉)
              </span>
            )}
            . Payment can be completed during the <strong>first 3 classes</strong>, so the student
            can experience the class before paying.
          </p>

          <div style={{ background: 'var(--navy)', borderRadius: 20, padding: 24, marginBottom: 24 }}>
            <p style={{ color: 'rgba(255,255,255,0.82)', fontSize: 14, marginBottom: 16 }}>
              For faster confirmation, send a WhatsApp message now.
            </p>
            <button
              className="btn-gold"
              style={{
                width: '100%',
                fontSize: 15,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
              onClick={() =>
                window.open(`https://wa.me/${WHATSAPP_NUM}?text=${encodeURIComponent(waMsg)}`, '_blank')
              }
            >
              <img src="/whatsapp.png" alt="WhatsApp" style={{ height: 20, marginRight: 8 }} />
              Send WhatsApp Message
            </button>
          </div>

          <button
            className="btn-secondary"
            onClick={() => {
              setStatus('idle');
              setForm({
                name: '', parent: '', phone: '', email: '',
                cls: '', laptop: '', courses: [],
                batchDate: '', timing: '', note: '',
              });
              setCouponInput('');
              setCouponApplied(false);
              setCouponError('');
            }}
          >
            Register Another Student
          </button>
        </div>
      </div>
    );
  }

  // ── main form ──────────────────────────────────────────────────
  return (
    <div style={{ paddingTop: 64 }}>
      {/* Hero */}
      <section
        style={{
          background: 'linear-gradient(135deg, #0d2045, #1B3A6B, #2A5298)',
          padding: '68px 24px 52px',
          textAlign: 'center',
          color: '#fff',
        }}
      >
        <div
          className="badge"
          style={{
            background: 'rgba(240,180,41,0.2)',
            color: '#F0B429',
            border: '1.5px solid rgba(240,180,41,0.38)',
          }}
        >
          New Batch Registration Open
        </div>
        <h1
          className="serif"
          style={{ fontSize: 'clamp(28px, 5vw, 46px)', fontWeight: 800, margin: '16px 0 14px' }}
        >
          Register for Course
        </h1>
        <p
          style={{
            color: 'rgba(255,255,255,0.82)',
            fontSize: 17,
            maxWidth: 620,
            margin: '0 auto',
            lineHeight: 1.7,
          }}
        >
          First class is treated as demo. Fees are shown in the form and payment can be made in
          the first 3 classes.
        </p>
        <div
          style={{
            display: 'flex',
            gap: 20,
            justifyContent: 'center',
            flexWrap: 'wrap',
            marginTop: 32,
          }}
        >
          {['5 batch dates', '8 seats per batch', 'Pay in first 3 classes', 'Limited seats'].map(
            (text) => (
              <span
                key={text}
                style={{
                  background: 'rgba(255,255,255,0.12)',
                  padding: '8px 18px',
                  borderRadius: 50,
                  fontSize: 14,
                  fontWeight: 700,
                  border: '1px solid rgba(255,255,255,0.2)',
                }}
              >
                {text}
              </span>
            )
          )}
        </div>
      </section>

      {/* Form */}
      <section className="section" style={{ background: 'var(--cream)' }}>
        <div className="container-sm">
          <div className="card" style={{ padding: '44px 40px' }}>
            <h2
              style={{
                color: 'var(--navy)',
                fontWeight: 900,
                fontSize: 22,
                marginBottom: 6,
                textAlign: 'center',
              }}
            >
              Course Registration Form
            </h2>
            <p style={{ color: 'var(--gray)', textAlign: 'center', marginBottom: 26, fontSize: 14 }}>
              New Batches start every Monday, Wednesday and Friday. Only 8 students per batch.
            </p>

            {status === 'error' && (
              <div
                style={{
                  background: '#fff0f0',
                  border: '1.5px solid #f5c6c6',
                  borderRadius: 10,
                  padding: '12px 16px',
                  marginBottom: 20,
                  color: '#c0392b',
                  fontSize: 14,
                }}
              >
                Something went wrong. Please try again or WhatsApp us directly.
              </div>
            )}

            {/* ── Personal fields ── */}
            <Field label="Student Name *" error={errors.name}>
              <input
                className="form-input"
                placeholder="Enter student's full name"
                value={form.name}
                onChange={set('name')}
              />
            </Field>

            <Field label="Parent / Guardian Name *" error={errors.parent}>
              <input
                className="form-input"
                placeholder="Enter parent's full name"
                value={form.parent}
                onChange={set('parent')}
              />
            </Field>

            <Field label="WhatsApp Phone Number *" error={errors.phone}>
              <input
                className="form-input"
                type="tel"
                maxLength={10}
                placeholder="10-digit mobile number"
                value={form.phone}
                onChange={set('phone')}
              />
            </Field>

            <Field label="Email Address *" error={errors.email}>
              <input
                className="form-input"
                type="email"
                placeholder="your@email.com (for confirmation)"
                value={form.email}
                onChange={set('email')}
              />
            </Field>

            <Field label="Current Class / Education *" error={errors.cls}>
              <select className="form-input" value={form.cls} onChange={set('cls')}>
                <option value="">Select class</option>
                {CLASSES.map((c) => (
                  <option key={c}>{c}</option>
                ))}
              </select>
            </Field>

            <Field label="Do you have a Laptop/Computer? *" error={errors.laptop}>
              <select className="form-input" value={form.laptop} onChange={set('laptop')}>
                <option value="">Select</option>
                <option>Yes, I have one</option>
                <option>No, I do not have one yet</option>
                <option>I will arrange one before joining</option>
              </select>
              {form.laptop === 'No, I do not have one yet' && (
                <div
                  style={{
                    marginTop: 8,
                    background: '#fff4e5',
                    border: '1.5px solid #f5c27a',
                    borderRadius: 10,
                    padding: '10px 12px',
                    fontSize: 13,
                    color: '#8a5a00',
                    fontWeight: 600,
                    lineHeight: 1.6,
                  }}
                >
                  ⚠️ Laptop/Computer is mandatory to learn these courses. Kindly arrange one before the class starts.
                </div>
              )}
            </Field>

            {/* ── Course Selection (card grid) ── */}
            <div className="form-group">
              <label className="form-label">Course Selection * <span style={{ fontWeight: 600, fontSize: 13, color: 'var(--gray)' }}>(select one or more)</span></label>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))',
                  gap: 12,
                  marginTop: 4,
                }}
              >
                {COURSE_OPTIONS.map(({ id, icon, title, subtitle, price }) => {
                  const selected = form.courses.includes(id);
                  return (
                    <button
                      key={id}
                      type="button"
                      onClick={() => toggleCourse(id)}
                      style={{
                        padding: '16px 14px',
                        borderRadius: 14,
                        border: selected
                          ? '2px solid var(--navy, #1B3A6B)'
                          : '2px solid #d8dde6',
                        background: selected ? 'var(--navy, #1B3A6B)' : '#fff',
                        color: selected ? '#fff' : '#333',
                        cursor: 'pointer',
                        textAlign: 'left',
                        transition: 'all 0.15s ease',
                        fontFamily: 'inherit',
                        position: 'relative',
                      }}
                    >
                      {/* Checkmark badge */}
                      {selected && (
                        <div
                          style={{
                            position: 'absolute',
                            top: 10,
                            right: 10,
                            width: 20,
                            height: 20,
                            borderRadius: '50%',
                            background: '#F0B429',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: 11,
                            fontWeight: 900,
                            color: 'var(--navy)',
                          }}
                        >
                          ✓
                        </div>
                      )}
                      {/* Icon */}
                      <div style={{ fontSize: 26, marginBottom: 8 }}>{icon}</div>
                      {/* Title */}
                      <div
                        style={{
                          fontSize: 14,
                          fontWeight: 800,
                          lineHeight: 1.3,
                          marginBottom: 6,
                          paddingRight: selected ? 20 : 0,
                        }}
                      >
                        {title}
                      </div>
                      {/* Subtitle */}
                      <div
                        style={{
                          fontSize: 11,
                          fontWeight: 600,
                          opacity: selected ? 0.75 : 0.5,
                          marginBottom: 10,
                          lineHeight: 1.5,
                        }}
                      >
                        {subtitle}
                      </div>
                      {/* Price */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                        <span
                          style={{
                            background: selected
                              ? 'rgba(240,180,41,0.25)'
                              : 'rgba(27,58,107,0.08)',
                            color: selected ? '#F0B429' : 'var(--navy)',
                            padding: '3px 10px',
                            borderRadius: 50,
                            fontSize: 13,
                            fontWeight: 800,
                          }}
                        >
                          {fmt(price)}
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>
              {errors.courses && (
                <div className="form-error" style={{ marginTop: 6 }}>
                  Error: {errors.courses}
                </div>
              )}
            </div>

            {/* ── Price Summary + Coupon ── */}
            {form.courses.length > 0 && (
              <div
                style={{
                  border: '1.5px solid #d8dde6',
                  borderRadius: 14,
                  overflow: 'hidden',
                  marginBottom: 22,
                }}
              >
                {/* Header */}
                <div
                  style={{
                    background: 'var(--navy)',
                    padding: '12px 18px',
                    color: '#fff',
                    fontWeight: 800,
                    fontSize: 14,
                  }}
                >
                  Fee Summary
                </div>

                <div style={{ padding: '18px 18px 14px', background: '#fff' }}>
                  {/* Per course lines */}
                  {form.courses.map((id) => {
                    const course = COURSE_OPTIONS.find((c) => c.id === id);
                    return (
                      <div
                        key={id}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          fontSize: 14,
                          color: 'var(--gray)',
                          marginBottom: 8,
                        }}
                      >
                        <span>{course.icon} {course.title}</span>
                        <span style={{ fontWeight: 700, color: 'var(--navy)' }}>
                          {fmt(course.price)}
                        </span>
                      </div>
                    );
                  })}

                  {/* Divider */}
                  <div style={{ borderTop: '1px solid #eee', margin: '10px 0' }} />

                  {/* Total row */}
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      fontSize: 14,
                      color: 'var(--gray)',
                      marginBottom: couponApplied ? 6 : 0,
                    }}
                  >
                    <span style={{ fontWeight: 700 }}>Total</span>
                    <span
                      style={{
                        fontWeight: 800,
                        color: couponApplied ? 'var(--gray)' : 'var(--navy)',
                        textDecoration: couponApplied ? 'line-through' : 'none',
                        fontSize: couponApplied ? 13 : 15,
                      }}
                    >
                      {fmt(originalTotal)}
                    </span>
                  </div>

                  {/* Discount row */}
                  {couponApplied && (
                    <>
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          fontSize: 14,
                          color: '#1a5a2e',
                          fontWeight: 700,
                          marginBottom: 6,
                        }}
                      >
                        <span>🏷️ Coupon ({SUMMER_COUPON_CODE}) — 50% off</span>
                        <span>− {fmt(discountAmt)}</span>
                      </div>
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          fontSize: 17,
                          fontWeight: 900,
                          color: 'var(--navy)',
                          borderTop: '1.5px solid #d8dde6',
                          paddingTop: 10,
                          marginTop: 4,
                        }}
                      >
                        <span>You Pay</span>
                        <span style={{ color: '#1a5a2e' }}>{fmt(finalTotal)} 🎉</span>
                      </div>
                    </>
                  )}

                  {/* Coupon input */}
                  <div style={{ marginTop: 14 }}>
                    {!couponApplied ? (
                      <>
                        <div
                          style={{
                            fontSize: 13,
                            fontWeight: 700,
                            color: 'var(--navy)',
                            marginBottom: 6,
                          }}
                        >
                          Have a coupon code?
                        </div>
                        <div style={{ display: 'flex', gap: 8 }}>
                          <input
                            className="form-input"
                            placeholder={`e.g. ${SUMMER_COUPON_CODE}`}
                            value={couponInput}
                            onChange={(e) => {
                              setCouponInput(e.target.value.toUpperCase());
                              setCouponError('');
                            }}
                            onKeyDown={(e) => e.key === 'Enter' && applyCoupon()}
                            style={{
                              flex: 1,
                              marginBottom: 0,
                              fontFamily: 'monospace',
                              fontWeight: 700,
                              letterSpacing: 1,
                              textTransform: 'uppercase',
                            }}
                          />
                          <button
                            type="button"
                            onClick={applyCoupon}
                            style={{
                              padding: '10px 18px',
                              background: 'var(--navy)',
                              color: '#fff',
                              border: 'none',
                              borderRadius: 10,
                              fontWeight: 800,
                              fontSize: 14,
                              cursor: 'pointer',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            Apply
                          </button>
                        </div>
                        {couponError && (
                          <div
                            style={{
                              marginTop: 6,
                              fontSize: 13,
                              color: '#c0392b',
                              fontWeight: 600,
                            }}
                          >
                            {couponError}
                          </div>
                        )}
                      </>
                    ) : (
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          background: 'rgba(26,90,46,0.08)',
                          border: '1.5px solid rgba(26,90,46,0.25)',
                          borderRadius: 10,
                          padding: '9px 14px',
                        }}
                      >
                        <span
                          style={{ color: '#1a5a2e', fontWeight: 800, fontSize: 14 }}
                        >
                          ✅ Coupon <span style={{ fontFamily: 'monospace' }}>{SUMMER_COUPON_CODE}</span> applied — 50% off!
                        </span>
                        <button
                          type="button"
                          onClick={removeCoupon}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: '#c0392b',
                            fontWeight: 700,
                            fontSize: 13,
                            cursor: 'pointer',
                            padding: '0 4px',
                          }}
                        >
                          Remove
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Payment note */}
                  <div
                    style={{
                      marginTop: 12,
                      color: 'var(--gray)',
                      fontSize: 13,
                      lineHeight: 1.6,
                    }}
                  >
                    Payment can be made in the first 3 classes so demo and class experience are
                    covered.
                  </div>
                </div>
              </div>
            )}

           {/* ── Batch Selection ── */}
            <Field label="Batch Start Date *" error={errors.batchDate}>
              <div style={{ marginTop: 4 }}>
                <BatchCalendar
                  selected={form.batchDate}
                  onChange={(val) => {
                    setForm((f) => ({ ...f, batchDate: val }));
                    setErrors((e) => ({ ...e, batchDate: undefined }));
                  }}
                />
              </div>
              {form.batchDate && (
                <div
                  style={{
                    marginTop: 10,
                    background: 'rgba(27,58,107,0.07)',
                    border: '1.5px solid rgba(27,58,107,0.2)',
                    borderRadius: 10,
                    padding: '9px 14px',
                    fontSize: 14,
                    fontWeight: 700,
                    color: 'var(--navy)',
                  }}
                >
                  📅 Selected: {form.batchDate}
                </div>
              )}
              <div style={{ marginTop: 8, color: '#8b1a1a', fontSize: 13, fontWeight: 700 }}>
                Only 8 students per batch — Mon, Wed &amp; Fri batches available.
              </div>
            </Field>

            {/* ── Optional fields ── */}
            <Field label="Preferred Timing (Optional)">
              <select className="form-input" value={form.timing} onChange={set('timing')}>
                <option value="">Select preferred time</option>
                {TIMINGS.map((t) => (
                  <option key={t}>{t}</option>
                ))}
              </select>
            </Field>

            <Field label="Any questions or notes? (Optional)">
              <textarea
                className="form-input"
                rows={3}
                placeholder="Anything you want us to know?"
                value={form.note}
                onChange={set('note')}
                style={{ resize: 'vertical' }}
              />
            </Field>

            <button
              className="btn-primary"
              style={{
                width: '100%',
                padding: '16px',
                fontSize: 16,
                marginTop: 8,
                opacity: status === 'sending' ? 0.7 : 1,
                cursor: status === 'sending' ? 'not-allowed' : 'pointer',
              }}
              onClick={handleSubmit}
              disabled={status === 'sending'}
            >
              {status === 'sending' ? 'Submitting...' : 'Register for Course'}
            </button>

            <div style={{ textAlign: 'center', marginTop: 16, color: 'var(--gray)', fontSize: 13 }}>
              We will contact you only for registration confirmation.
            </div>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="section-sm" style={{ background: 'var(--navy)' }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: 28 }}>
            <h3 style={{ color: '#fff', fontWeight: 800, fontSize: 20 }}>Quick FAQs</h3>
          </div>
          <div className="grid-3">
            {[
              [
                'When do I pay the fee?',
                'You can pay in the first 3 classes after attending the initial class/demo experience.',
              ],
              [
                'How many students per batch?',
                'Only 8 students are allowed in each batch for better personal attention.',
              ],
              [
                'Can I change my batch date?',
                'Yes. If seats are available, our team will help you move to another listed date.',
              ],
            ].map(([q, a]) => (
              <div
                key={q}
                style={{ background: 'rgba(255,255,255,0.07)', borderRadius: 16, padding: 24 }}
              >
                <div
                  style={{ fontWeight: 800, color: '#F0B429', marginBottom: 8, fontSize: 15 }}
                >
                  {q}
                </div>
                <div
                  style={{ color: 'rgba(255,255,255,0.75)', fontSize: 14, lineHeight: 1.65 }}
                >
                  {a}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}